if (!customElements.get('product-inventory')) {
  class ProductInventory extends HTMLElement {
    constructor() {
      super();
      if (this.hidden) {
        this.initLazySection();
      } else {
        window.initLazyScript(this, this.initLazySection.bind(this));
      }
    }

    initLazySection() {
      this.inventoryNotice = this.querySelector('.js-inventory-notice') || document.getElementById('product-inv-txt');
      this.urgencyMessage = this.querySelector('.js-inventory-urgency');
      this.indicatorBar = this.querySelector('.js-inventory-indicator');
      this.variantInventory = this.getVariantInventory();

      // Bind to on:variant:change event for this product
      const variantPicker = this.closest('.product-info')?.querySelector('variant-picker');
      if (variantPicker) {
        variantPicker.addEventListener('on:variant:change', this.handleVariantChange.bind(this));
      }

      // Find supplier flag for current variant (initial load)
      const currentVariantId = Number(this.dataset.variantId);
      const current = this.variantInventory?.find((v) => Number(v.id) === currentVariantId);

      // Init
      this.updateInventory(
        parseInt(this.dataset.inventoryQuantity, 10),
        this.dataset.variantAvailable === 'true',
        this.dataset.inventoryPolicy,
        !!current?.supplier_stock,
        !!current?.is_preorder,
        !!current?.is_bundle,
        current?.bundle_state || ''
      );
    }

    /**
     * Gets the inventory data for all product variants
     * @returns {?object}
     */
    getVariantInventory() {
      const dataEl = this.querySelector('[type="application/json"]');
      return this.variantInventory || JSON.parse(dataEl.textContent);
    }

    /**
     * Handles a 'change' event on the variant picker element
     * @param {object} evt - Event object
     */
    handleVariantChange(evt) {
      if (evt.detail.variant) {
        const inventory = this.variantInventory.filter(
          (variant) => variant.id === evt.detail.variant.id
        )[0];

        this.updateInventory(
          inventory.inventory_quantity,
          inventory.available,
          inventory.inventory_policy,
          !!inventory.supplier_stock,
          !!inventory.is_preorder,
          !!inventory.is_bundle,
          inventory.bundle_state || ''
        );
      } else {
        this.updateInventory(0, false);
        this.hidden = true;
      }
    }

    ownWarehouseInner(count) {
      if (count <= 0) return '';
      const t = typeof theme !== 'undefined' && theme.strings ? theme.strings : {};
      const exactTpl = t.ownStockCountExact || '';
      const capped = t.ownStockCountCapped || '10+';
      if (count > 10) return capped;
      return exactTpl.replace(/\[count\]/g, String(count));
    }

    /**
     *
     * Updates the inventory notice
     * @param {number} count - the inventory quantity available
     * @param {boolean} available - whether current variant is available
     * @param {string} inventoryPolicy - whether product continues selling when out of stock
     * @param {boolean} supplierStock - whether supplier has stock for this variant (metafield)
     */
    updateInventory(count, available, inventoryPolicy, supplierStock = false, isPreorder = false, isBundle = false, bundleState = '') {
      // Digital products: show bullet list, never overwrite with stock
      if (this.dataset.isDigital === 'true') {
        this.hidden = false;
        this.setAttribute('data-inventory-level', 'normal');
        if (this.indicatorBar) this.indicatorBar.hidden = true;
        return;
      }

      // Bundle-guard: bundle-varianter holder inventory > 0 (Bundles-appen), men når
      // en komponent kun er på fjernlager (supplier.stock=true) skal bundlen vises som
      // fjernlager - ikke "På eget lager". Behandl da som count <= 0.
      // bundleState kommer fra snippets/bundle-stock-state.liquid og er 'own' når hver eneste
      // komponent har eget lager; da er supplier.stock-flaget forældet og guarden skal ikke slå til.
      const bundleRemote = isBundle && supplierStock && bundleState !== 'own';

      // "På leverandørlager" når vi har 0 på eget lager (eller remote bundle), men leverandørflag er true
      const stockAtSupplier = ((count <= 0 || bundleRemote) && supplierStock);

      this.setAttribute('data-stock-at-supplier', stockAtSupplier ? 'true' : 'false');

      if (count === 0 && !available && this.hidden) {
        return;
      }

      let inventoryLevel;

      if (count <= 0 || bundleRemote) {
        if (bundleRemote) {
          inventoryLevel = 'backordered';
        } else if (inventoryPolicy === 'continue') {
          inventoryLevel = 'backordered';
        } else if (available) {
          inventoryLevel = 'in_stock';
        } else {
          inventoryLevel = 'none';
        }
      } else if (count <= parseInt(this.dataset.thresholdVeryLow, 10)) {
        inventoryLevel = 'very_low';
      } else if (count <= parseInt(this.dataset.thresholdLow, 10)) {
        inventoryLevel = 'low';
      } else {
        inventoryLevel = 'normal';
      }

      // En pakke med en komponent der slet ikke kan købes, kan ikke leveres - uanset hvad
      // pakkevariantens eget lagertal siger. Købsknappen er slået fra i main-product.liquid.
      // Attributten styrer CSS'en: leveringslinjen vises normalt kun for 'backordered', og
      // uden den forsvandt hele lagerstatussen fra siden i stedet for at sige "Udsolgt".
      const bundleUnavailable = isBundle && bundleState === 'unavailable';
      if (bundleUnavailable) {
        inventoryLevel = 'none';
      }
      this.setAttribute('data-bundle-unavailable', bundleUnavailable ? 'true' : 'false');

      if (
        ((this.dataset.showNotice === 'always'
          || (this.dataset.showNotice === 'low' && inventoryLevel.includes('low')))
          && !(inventoryLevel === 'backordered' && this.dataset.showNoStockBackordered === 'false'))
      ) {
        this.hidden = false;

        // Set the inventory level data attribute
        this.setAttribute('data-inventory-level', inventoryLevel);

        // Toggle "På fjernlager" notice below shipping (show below when supplier has stock, above when Bestillingsvare)
        const showFjernlagerBelow = isPreorder || (inventoryLevel === 'backordered' && this.dataset.stockAtSupplier === 'true');
        const container = this.closest('.product-info') || this.closest('[class*="product-info"]');
        const fjernlagerBelow = container?.querySelector('.product-inventory__fjernlager-below');
        if (fjernlagerBelow) {
          fjernlagerBelow.hidden = !showFjernlagerBelow;
        }
        if (container) {
          container.classList.toggle('show-fjernlager-below', showFjernlagerBelow);
        }
        // Synk countdown-wrapper i samme frame så layout ikke hopper (MutationObserver kører async)
        const countdownWrapper = document.getElementById('countdown-wrapper');
        if (countdownWrapper) {
          countdownWrapper.classList.toggle('is-visible', !showFjernlagerBelow);
        }
        const noticeEl = document.getElementById('product-inv-txt');
        const leveringsTidEl = document.getElementById('cd_leverings_tid');
        if (noticeEl && leveringsTidEl) {
      const combinedLineEl = noticeEl.closest('#cd_leverings_tid') ? leveringsTidEl : noticeEl;
      combinedLineEl.classList.toggle('custom-checkmark', !showFjernlagerBelow && !isPreorder);
      noticeEl.classList.toggle('product-inventory__status', showFjernlagerBelow || isPreorder);

      if (isPreorder) {
      noticeEl.innerText = '';
      }
      }

        if (this.inventoryNotice) {
          this.inventoryNotice.setAttribute('data-inventory', count);
        }

        // Determine whether to show the count or not (skip count when data-hide-count for combined line)
        const hideCount = this.dataset.hideCount === 'true';
        if (this.inventoryNotice) {
          if (inventoryLevel === 'backordered') {
            this.inventoryNotice.innerText = '';
          } else if (
            count > 0
            && ['normal', 'low', 'very_low'].includes(inventoryLevel)
          ) {
            const useInStockOnlyLabel =
              !hideCount
              && inventoryLevel !== 'in_stock'
              && (this.dataset.showCount === 'always'
                || (this.dataset.showCount === 'low' && inventoryLevel.includes('low')));
            let label;
            if (useInStockOnlyLabel) {
              label = theme?.strings?.inStock || 'In stock';
            } else if (inventoryLevel === 'very_low') {
              label = theme.strings.veryLowStock;
            } else if (inventoryLevel === 'low') {
              label = theme.strings.lowStock;
            } else {
              label = theme.strings.inStock;
            }
            const inner = this.ownWarehouseInner(count);
            this.inventoryNotice.innerHTML = `<span style="color: rgb(42 43 42);">${label}</span> (${inner})`;
          } else if (inventoryLevel === 'in_stock') {
            this.inventoryNotice.innerText = theme.strings.inStock;
          } else if (inventoryLevel === 'none') {
            this.inventoryNotice.innerText = theme.strings.noStock;
          }
        }

        // Update urgency message if needed
        if (this.urgencyMessage) {
          if (inventoryLevel === 'very_low') {
            this.urgencyMessage.innerHTML = this.dataset.textVeryLow;
          } else if (inventoryLevel === 'low') {
            this.urgencyMessage.innerHTML = this.dataset.textLow;
          } else if (inventoryLevel === 'backordered') {
            this.urgencyMessage.innerHTML = this.dataset.textNoStockBackordered;
          } else if (inventoryLevel === 'normal' || inventoryLevel === 'in_stock') {
            this.urgencyMessage.innerHTML = this.dataset.textNormal;
          } else if (inventoryLevel === 'none') {
            this.urgencyMessage.innerHTML = this.dataset.textNoStock;
          }
        }

        // Update the bar indicator
        if (this.indicatorBar) {
          this.indicatorBar.hidden = false;

          let newWidth;
          if ((count >= this.dataset.scale || inventoryLevel === 'in_stock') && inventoryLevel !== 'backordered') {
            newWidth = 100;
          } else {
            newWidth = ((100 / parseInt(this.dataset.scale, 10)) * count).toFixed(1);
          }
          this.indicatorBar.querySelector('span').style.width = `${newWidth}%`;
        }
      } else {
        this.hidden = true;
        const container = this.closest('.product-info') || this.closest('[class*="product-info"]');
        const fjernlagerBelow = container?.querySelector('.product-inventory__fjernlager-below');
        if (fjernlagerBelow) fjernlagerBelow.hidden = true;
        if (container) container.classList.remove('show-fjernlager-below');
        const countdownWrapper = document.getElementById('countdown-wrapper');
        if (countdownWrapper) countdownWrapper.classList.remove('is-visible');
      }
    }
  }

  customElements.define('product-inventory', ProductInventory);
} 